import React, { useRef, useState } from 'react';
import { Col, Row } from '@/lib/utils';
import { DemiLightText, RegularText } from '@/lib/Text';
import DialogShell from '../exceptionRates/DialogShell';
import SelectMenu from '@/components/DropDown/SelectMenu';
import {
    CorrectMembershipSettlementResponse,
    SettlementCategory,
    SettlementIssueKind,
    getSettlementClassificationErrorMessage,
    useCorrectMembershipSettlementBasis,
} from '@/features/settlementClassification';
import { buildSettlementCategoryOptions } from '../../../ClassPass/SettlementCategory/settlementCategoryOptions';
import { BASE_SETTLEMENT_KIND_NOTE } from '@/features/settlementClassification/settlementBasis';

const ISSUE_KIND_OPTIONS = [
    { value: 'new', label: '신규' },
    { value: 'reRegistration', label: '재등록' },
    { value: '', label: '정해지지 않음' },
];

const SELECT_CLASS =
    'h-[42px] w-full border border-solid border-[#E6E8EA] rounded-[10px] bg-white px-[12px] text-[15px] text-[#2D3642] outline-none focus:border-primary';

/**
 * 수강권 정산 기준 정정(관리자 전용). 고친 값만 보낸다.
 * 서버는 이 수강권과 강사 인계로 이어진 하위 세대(같은 센터·같은 회원)를 함께 고치고,
 * 정산은 수강권 값을 그때그때 읽어서 지난 달 급여 계산도 바뀐다 — 확인 단계에서 두 가지를 알린다.
 */
const ModalCorrectSettlementBasis = ({
    centerId,
    membershipId,
    currentCategoryKey: latestCategoryKey,
    currentIssueKind: latestIssueKind,
    categories,
    issueKindRecorded: latestIssueKindRecorded = true,
    categoryOnly = false,
    onClose,
    onCorrected,
}: {
    centerId: string;
    membershipId: string;
    currentCategoryKey: string | null;
    currentIssueKind: SettlementIssueKind;
    categories: SettlementCategory[];
    /**
     * 수강권에 발급 유형(settlementIssueKind) 키가 있는가. 없는 옛 수강권은 서버가 지금의 신규/재등록 표시로 재등록 수업료를
     * 정하는데, 발급 유형을 한 번 정정하면 키가 생겨(null 이어도) 그 상태로는 돌아가지 않는다 — 「되돌릴 수 있어요」라고 하지 않는다.
     */
    issueKindRecorded?: boolean;
    /**
     * 기본 정산: 분류만 고친다. 서버 base 는 발급 유형을 보지 않고 수강권의 신규·재등록 표시(isNewSales)로 정하므로
     * 발급 유형 칸을 보이지 않고 보내지도 않는다(정정 API 는 분류만 담은 본문을 받는다 — Joi .or).
     */
    categoryOnly?: boolean;
    onClose: () => void;
    onCorrected: (patch: Record<string, unknown>) => void;
}) => {
    /*
     * 편집 기준값은 창을 연 때 값으로 고정한다(5회차 지적 3). 창이 열린 동안 수강권 정보가 다시 받아져도(정정 뒤 재조회 등)
     * changed 판정·PATCH 본문·확인 문구는 이 기준으로만 만든다 — 부분 실패(원본만 바뀌고 하위 실패) 뒤 같은 정정을 다시 할 수
     * 있어야 하고, 손대지 않은 항목(예: 유형만 바꿨는데 분류가 재조회로 바뀜)이 PATCH 에 섞이면 안 된다.
     * 창을 닫았다 다시 열면 그때 최신값이 기준이 된다.
     */
    const [currentCategoryKey] = useState<string | null>(latestCategoryKey);
    const [currentIssueKind] = useState<SettlementIssueKind>(latestIssueKind);
    const [issueKindRecorded] = useState<boolean>(latestIssueKindRecorded);
    const [categoryKey, setCategoryKey] = useState<string | null>(currentCategoryKey);
    const [issueKind, setIssueKind] = useState<SettlementIssueKind>(currentIssueKind);
    const [reason, setReason] = useState('');
    const [step, setStep] = useState<'edit' | 'confirm' | 'done'>('edit');
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<CorrectMembershipSettlementResponse | null>(null);
    const correct = useCorrectMembershipSettlementBasis(centerId);
    const busy = correct.isLoading;

    /*
     * 미해결 필드(7회차 — 비교 규칙 대신 근본 처리):
     * 직전에 실패한 정정 요청에 들어 있던 필드는 서버가 일부(원본부터 순차)만 바꿨을 수 있다. 이 창에서 그 필드는 「미해결」로
     * 표시하고, 다음 요청에는 비교와 무관하게 **편집값 그대로 항상 포함**한다(서버는 지금 값과 같은 값도 거부하지 않고, 이미
     * 같은 수강권은 「변경 없음」으로 넘긴다 — settlementSnapshotCorrection.service applyCorrection). 성공하면 해제한다.
     * 미해결이 아닌 필드는 창을 연 때 값과 다를 때만 포함한다(재조회로 바뀐 값이 손대지 않은 필드에 섞이지 않게).
     */
    const [unresolvedFields, setUnresolvedFields] = useState<{ category: boolean; kind: boolean }>({
        category: false,
        kind: false,
    });
    const categoryChanged = unresolvedFields.category || categoryKey !== currentCategoryKey;
    const currentCategoryArchived = !!categories.find((c) => c.key === currentCategoryKey)?.archived;
    const kindChanged = unresolvedFields.kind || issueKind !== currentIssueKind;
    const changed = categoryChanged || kindChanged;

    // 연타 방지 — isLoading 은 다음 렌더에야 반영돼 그 사이 두 번째 클릭이 들어올 수 있다.
    const submittingRef = useRef(false);
    const submit = async () => {
        if (submittingRef.current) return;
        submittingRef.current = true;
        setError(null);
        try {
            const res = await correct.mutateAsync({
                membershipId,
                body: {
                    ...(kindChanged ? { settlementIssueKind: issueKind } : {}),
                    ...(categoryChanged ? { settlementCategoryKey: categoryKey } : {}),
                    ...(reason.trim() ? { reason: reason.trim() } : {}),
                },
            });
            onCorrected({
                ...(kindChanged ? { settlementIssueKind: issueKind, settlementIssueKindSource: 'corrected' } : {}),
                ...(categoryChanged ? { settlementCategoryKey: categoryKey } : {}),
            });
            setUnresolvedFields({ category: false, kind: false });
            setResult(res);
            setStep('done');
        } catch (e) {
            // 이번에 보낸 필드는 미해결 — 서버가 일부만 바꿨을 수 있어 다음 요청에 항상 다시 싣는다.
            setUnresolvedFields((prev) => ({ category: prev.category || categoryChanged, kind: prev.kind || kindChanged }));
            setStep('edit');
            setError(getSettlementClassificationErrorMessage(e as any));
        } finally {
            submittingRef.current = false;
        }
    };

    const categoryLabel = (key: string | null) =>
        !key ? '미분류' : categories.find((c) => c.key === key)?.name ?? '알 수 없는 분류';
    const kindLabel = (kind: SettlementIssueKind) =>
        kind === 'new' ? '신규' : kind === 'reRegistration' ? '재등록' : '정해지지 않음';

    const updatedCount = result?.updatedMembershipIds?.length ?? 0;
    const isDescendant = !!result?.rootMembershipId && result.rootMembershipId !== membershipId;

    return (
        <DialogShell title="정산 기준 수정" width={460} onClose={onClose} busy={busy}>
            <Col className="px-[24px] pb-[22px]">
                {step === 'edit' && (
                    <>
                        <DemiLightText fontSize={13} color="#6B7280" className="mt-[10px] mb-[6px]">
                            정산 분류
                        </DemiLightText>
                        {/* 기본 <select> 는 맥 크롬에서 위로 펼쳐져 위쪽 문구를 가려 아래로 열리는 목록으로 바꿨다(값 계약은 같다) */}
                        <SelectMenu
                            aria-label="정산 분류"
                            className="w-full"
                            value={categoryKey ?? ''}
                            options={[
                                { value: '', label: '미분류' },
                                ...buildSettlementCategoryOptions(categories, currentCategoryKey).map((o) => ({
                                    value: o.value,
                                    label: o.label,
                                })),
                            ]}
                            onChange={(value) => setCategoryKey(value || null)}
                        />
                        {categoryOnly ? (
                            <DemiLightText fontSize={13} lineHeight={19} color="#6B7280" className="mt-[8px]">
                                {BASE_SETTLEMENT_KIND_NOTE}
                            </DemiLightText>
                        ) : (
                            <>
                                <DemiLightText fontSize={13} color="#6B7280" className="mt-[14px] mb-[6px]">
                                    발급 유형
                                </DemiLightText>
                                <SelectMenu
                                    aria-label="발급 유형"
                                    className="w-full"
                                    value={issueKind ?? ''}
                                    options={ISSUE_KIND_OPTIONS}
                                    onChange={(value) => setIssueKind((value || null) as SettlementIssueKind)}
                                />
                            </>
                        )}
                        <DemiLightText fontSize={13} color="#6B7280" className="mt-[14px] mb-[6px]">
                            고치는 이유 (선택)
                        </DemiLightText>
                        <input
                            className={SELECT_CLASS}
                            value={reason}
                            maxLength={500}
                            placeholder="예: 재등록인데 신규로 판매함"
                            onChange={(e) => setReason(e.target.value)}
                        />
                        {error && (
                            <RegularText fontSize={14} color="#D93043" className="mt-[12px]">
                                {error}
                            </RegularText>
                        )}
                    </>
                )}

                {step === 'confirm' && (
                    <Col className="mt-[12px]">
                        <Col className="rounded-[10px] bg-[#F6F8FA] px-[14px] py-[10px] gap-[2px]">
                            {categoryChanged && (
                                <RegularText fontSize={14} lineHeight={21} color="#3D4244">
                                    {categoryKey === currentCategoryKey
                                        ? `정산 분류: 「${categoryLabel(categoryKey)}」 그대로 다시 보내요(앞선 정정이 일부만 됐을 수 있어요)`
                                        : `정산 분류: ${categoryLabel(currentCategoryKey)} → ${categoryLabel(categoryKey)}`}
                                </RegularText>
                            )}
                            {kindChanged && (
                                <RegularText fontSize={14} lineHeight={21} color="#3D4244">
                                    {issueKind === currentIssueKind
                                        ? `발급 유형: 「${kindLabel(issueKind)}」 그대로 다시 보내요(앞선 정정이 일부만 됐을 수 있어요)`
                                        : `발급 유형: ${kindLabel(currentIssueKind)} → ${kindLabel(issueKind)}`}
                                </RegularText>
                            )}
                        </Col>
                        <RegularText fontSize={14} lineHeight={22} color="#3D4244" className="mt-[12px] whitespace-pre-line">
                            {[
                                '· 강사 인계나 회차 나누기로 이어진 같은 회원의 수강권도 함께 바뀌어요.',
                                '· 이 수강권의 지난 달 급여 계산도 다시 계산돼요.',
                                /*
                                 * 「되돌릴 수 있어요」는 조건부로만 말한다(서버 settlementSnapshotCorrection·ptTierScheduleFee, 10/19 교차검증 D).
                                 *  - 같은 창에서 원래 값으로 다시 정정할 수는 있다. 하지만 이어진 수강권은 모두 그 값으로 맞춰지고(원래 서로
                                 *    달랐어도), 정정 표시는 남아 이후 상품 일괄 적용에서 「직접 고친 수강권」으로 빠진다 — 완전한 원상복구가 아니다.
                                 *  - 발급 유형 기록이 없는 옛 수강권은 유형을 한 번 정하면 「표시를 따르는」 상태로 돌아갈 수 없다.
                                 *  - 지금 분류가 보관된 분류면 되살리기 전에는 다시 고를 수 없다.
                                 */
                                kindChanged && !issueKindRecorded
                                    ? '· 발급 기록이 없는 옛 수강권이라, 발급 유형을 한 번 정하면 지금처럼 「신규/재등록 표시를 따르는」 상태로는 되돌릴 수 없어요.'
                                    : '· 잘못 고쳤으면 수강권 정보의 「수정」으로 원래 값을 다시 고를 수 있어요. 다만 이어진 수강권은 모두 그 값으로 맞춰지고, 이후 상품 일괄 적용에서는 직접 고친 수강권으로 남아요.',
                                categoryChanged && currentCategoryArchived
                                    ? `· 지금 분류 「${categoryLabel(currentCategoryKey)}」는 보관된 분류라, 다시 고르려면 먼저 분류 관리에서 되살려야 해요.`
                                    : '',
                            ]
                                .filter(Boolean)
                                .join('\n')}
                        </RegularText>
                    </Col>
                )}

                {step === 'done' && (
                    <Col className="mt-[12px] gap-[4px]">
                        <RegularText fontSize={15} lineHeight={23} color="#3D4244">
                            {updatedCount > 0 ? `수강권 ${updatedCount}개를 고쳤어요.` : '이미 같은 값이라 바뀐 수강권이 없어요.'}
                        </RegularText>
                        {/* 건너뛴 수강권(skippedMembershipIds — 다른 센터로 넘어간 것)의 개수는 보이지 않는다(10/19, 다른 센터 흔적 비노출). */}
                        {isDescendant && (
                            <DemiLightText fontSize={13} color="#6B7280">
                                강사를 바꾸기 전 수강권은 바뀌지 않았어요.
                            </DemiLightText>
                        )}
                    </Col>
                )}

                <Row className="justify-end gap-[8px] mt-[20px]">
                    {step !== 'done' && (
                        <div
                            className="h-[40px] w-[88px] rounded-[9px] bg-[#F4F5F7] flex items-center justify-center cursor-pointer"
                            onClick={busy ? undefined : step === 'confirm' ? () => setStep('edit') : onClose}
                        >
                            <RegularText fontSize={15} color="#3D4244">
                                {step === 'confirm' ? '이전' : '취소'}
                            </RegularText>
                        </div>
                    )}
                    <div
                        className={`h-[40px] px-[20px] rounded-[9px] bg-primary flex items-center justify-center ${
                            (step === 'edit' && !changed) || busy ? 'bg-opacity-30 cursor-not-allowed' : 'cursor-pointer'
                        }`}
                        onClick={
                            busy
                                ? undefined
                                : step === 'edit'
                                  ? changed
                                      ? () => setStep('confirm')
                                      : undefined
                                  : step === 'confirm'
                                    ? submit
                                    : onClose
                        }
                    >
                        <RegularText fontSize={15} color="white">
                            {step === 'edit' ? '다음' : step === 'confirm' ? (busy ? '고치는 중…' : '고치기') : '닫기'}
                        </RegularText>
                    </div>
                </Row>
            </Col>
        </DialogShell>
    );
};

export default ModalCorrectSettlementBasis;
